import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { BarterWarning } from '../barter/BarterWarning';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { isDay } from '../planning/wizard';
import type { CreatedInPanel } from './afterCreate';
import { NewPanelHead } from './NewPanelHead';
import { useCalendarLink, type CalendarLink } from './calendarLink';
import { datesOfPick, pickOfDates } from './pickDates';
import { ClientField, DaysFields, PickedDaysField, type Days } from './EventFields';
import { datesToInstants, EVENTS_RESOURCE, runsOf, segmentsFromDays, type EventView } from './labels';
import { refreshEvents } from './refresh';
import { emptyRepeat, requestKey, RepeatSection, type RepeatDraft } from './RepeatForm';
import { leaveWithoutAsking, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { ViewersFormField } from './ViewersField';
import '../planning/planning.css';
import { NotesInput } from '../ui/NotesInput';

type Picked = { id: string; name: string };

/**
 * "Nowe wypożyczenie" (F28, owner remark 25 + answer 4): our gear rented to another company, usually without crew.
 * In the side panel of "Wydarzenia" (owner 2026-10-21 23:31), shorter than "Nowe wydarzenie": client, days out and
 * back (with hours). "Utwórz i dodaj sprzęt" makes an event of kind "wypozyczenie" and the panel shows its card on
 * the tab "Sprzętówka" (reservations, shortages, "Dopożycz", "Przejdź do kosztorysu" — owner 00:38); quote,
 * statuses, return and crew — all from that card.
 */
export function NewRentalForm({
  initialDay = null,
  initialDays = [],
  onCreated,
  onClose,
  full = false,
  onFullChange,
  phone = false,
  calendar,
}: {
  initialDay?: string | null | undefined;
  initialDays?: readonly string[] | undefined;
  onCreated: (created: CreatedInPanel) => void;
  onClose: () => void;
  full?: boolean;
  onFullChange?: ((full: boolean) => void) | undefined;
  phone?: boolean;
  /** The calendar beside the panel picks the days out and back (owner 2026-10-22 14:40); the other fields stay. */
  calendar?: CalendarLink | undefined;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const day = initialDay;
  // owner 2026-10-21 00:44: days picked with Shift / Cmd (`dni=`): one run is the days out and back, runs with gaps are terms
  const [picked, setPicked] = useState<string[]>(() => [...initialDays].filter(isDay));
  const runs = runsOf(picked);
  const [client, setClient] = useState<Picked | null>(null);
  const [name, setName] = useState('');
  const [days, setDays] = useState<Days>(() =>
    runs.length > 0 ? { firstDay: runs[0]!.first, lastDay: runs.at(-1)!.last, firstTime: '10:00', lastTime: '' } : { firstDay: isDay(day) ? day : '', lastDay: isDay(day) ? day : '', firstTime: '10:00', lastTime: '' },
  );
  const terms = runs.length > 1;
  useCalendarLink(calendar, pickOfDates({ firstDay: days.firstDay, lastDay: days.lastDay, days: terms ? picked : [] }), (chosen) => {
    const p = datesOfPick(chosen);
    setPicked(p.days);
    setDays((d) => ({ ...d, firstDay: p.firstDay, lastDay: p.lastDay }));
  });
  const [notes, setNotes] = useState('');
  // "Kto widzi" (spec 2026-10-23-widocznosc-wydarzen): the creator alone until changed (null = untouched: nothing sent)
  const [viewers, setViewers] = useState<string[] | null>(null);
  const [invalid, setInvalid] = useState('');
  // "Wydarzenie cykliczne" (owner 2026-10-21 06:58): e.g. the same rental every month; made after the gear step
  const [repeatOn, setRepeatOn] = useState(false);
  const [repeat, setRepeat] = useState<RepeatDraft>(() => emptyRepeat(me, days.firstDay));
  const [repeatKey] = useState(requestKey);

  const create = useMutation({
    mutationFn: () => {
      const sees = (f: string) => !!me && fieldVisible(me, EVENTS_RESOURCE, f);
      const body = {
        kind: 'wypozyczenie',
        name: name.trim() || `Wypożyczenie: ${client?.name ?? ''}`.trim(),
        ...(terms ? { segments: segmentsFromDays(picked, days.firstTime, days.lastTime) } : datesToInstants(days)),
        ...(client && sees('contractorId') ? { contractorId: client.id } : {}),
        ...(notes.trim() && sees('notes') ? { notes: notes.trim() } : {}),
        ...(viewers && sees('viewerIds') ? { viewerIds: viewers } : {}),
      };
      return api<EventView>('/api/events', { method: 'POST', body });
    },
    onSuccess: async (ev) => {
      await refreshEvents(queryClient);
      onCreated({
        eventId: ev.id,
        notice: 'Wypożyczenie utworzone. Dodaj sprzęt w tabeli albo zamknij kartę — dodasz go później.',
        tab: 'packlist',
        series: repeatOn ? { draft: repeat, firstDay: days.firstDay, key: repeatKey } : null,
        remind: true,
      });
    },
  });
  /** What is missing before creating; '' when nothing. */
  const missing = () => (!client && name.trim() === '' ? 'Wybierz klienta albo wpisz nazwę wypożyczenia' : !days.firstDay || !days.lastDay ? 'Podaj dzień wydania i dzień zwrotu' : '');
  // owner 2026-10-23 13:34: closing with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”; „Zapisz” creates the rental
  const now = { picked, client, name, days, notes, repeatOn, viewers };
  const [fresh] = useState(() => now);
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(now, fresh),
    save: async () => {
      setInvalid(missing());
      if (missing()) return false;
      await create.mutateAsync();
      return true;
    },
  });
  if (!me) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setInvalid(missing());
    if (missing()) return;
    create.mutate();
  }
  const error = create.error instanceof ApiError ? create.error : null;

  return (
    <section ref={guardRef} className="panel event-card wizard new-in-panel" aria-label="Nowe wypożyczenie" data-feature-id="events.rentals">
      <NewPanelHead title="Nowe wypożyczenie" full={full} onFullChange={onFullChange} phone={phone} onClose={onClose} />
      <ol className="wizard-steps" aria-label="Kroki kreatora">
        {['Klient i terminy', 'Sprzęt'].map((label, i) => (
          <li key={label} aria-current={i === 0 ? 'step' : undefined}>
            <span>
              <span className="wizard-step-no" aria-hidden="true">
                {i + 1}
              </span>
              {label}
            </span>
          </li>
        ))}
      </ol>
      <form className="wizard-form" aria-label="Krok 1 z 2: Klient i terminy" onSubmit={submit} noValidate>
        <h3>Klient i terminy</h3>
        <p className="muted">Wynajem naszego sprzętu innej firmie — zwykle bez obsługi. Sprzęt dodasz w następnym kroku, w zakładce „Sprzętówka” karty wypożyczenia.</p>
        {fieldVisible(me, EVENTS_RESOURCE, 'contractorId') && (
          <ClientField
            picked={client}
            onPick={(c) => {
              setClient(c);
              if (c && name.trim() === '') setName(`Wypożyczenie: ${c.name}`);
            }}
            canCreate={can(me, 'contractors.contractors', 'create')}
          />
        )}
        {client && <BarterWarning contractorId={client.id} contractorName={client.name} />}
        <label className="field">
          <span>Nazwa</span>
          <input type="text" maxLength={300} value={name} placeholder="np. Wypożyczenie: Agencja X" onChange={(e) => setName(e.target.value)} />
        </label>
        <div>
          <p className="muted small">Pierwszy dzień — wydanie sprzętu, ostatni dzień — zwrot.</p>
          {terms ? (
            <PickedDaysField days={picked} firstTime={days.firstTime} lastTime={days.lastTime} onTimes={(patch) => setDays((d) => ({ ...d, ...patch }))} onSingle={() => setPicked([])} />
          ) : (
            <DaysFields value={days} onChange={(patch) => setDays((d) => ({ ...d, ...patch }))} />
          )}
        </div>
        {fieldVisible(me, EVENTS_RESOURCE, 'notes') && (
          <label className="field">
            <span>Uwagi</span>
            <NotesInput maxLength={5000} value={notes} placeholder="np. odbiór własnym transportem" onChange={(e) => setNotes(e.target.value)} />
          </label>
        )}
        {fieldVisible(me, EVENTS_RESOURCE, 'viewerIds') && <ViewersFormField value={viewers ?? [me.id]} onChange={setViewers} />}
        <RepeatSection on={repeatOn} onToggle={setRepeatOn} draft={repeat} onChange={setRepeat} firstDay={days.firstDay} />
        {invalid && (
          <p className="alert" role="alert">
            {invalid}
          </p>
        )}
        {error && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="wizard-actions">
          <button className="btn ghost" type="button" onClick={() => leaveWithoutAsking(onClose)}>
            Anuluj
          </button>
          <span className="wizard-actions-end">
            <button className="btn primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Tworzenie…' : 'Utwórz i dodaj sprzęt'}
            </button>
          </span>
        </div>
      </form>
    </section>
  );
}
